import { Injectable } from '@angular/core';

import type { User } from '@core/account-types';
import { Department } from '@core/departments-types';
import type { Location } from '@core/locations-types';
import { JobBasic, JobItem, OfferBrief, OfferFull, OfferItem, OfferKind } from '@core/offers-types';
import type { PipelineTemplateItem } from '@core/pipeline-templates-types';

export type MapperAdditionalData = {
  department: Department | null;
  hiringManager: User | null;
  recruiter: User | null;
  locations: Location[];
  pipelineTemplate: Pick<PipelineTemplateItem, 'id' | 'stages'> | null;
};

@Injectable({ providedIn: 'root' })
export class MapperService {
  public static mapJobFullToJobBasic(jobFull: OfferFull): JobBasic {
    return {
      candidatesCount: jobFull.candidatesCount,
      careersJobPageLayoutId: jobFull.careersJobPageLayoutId,
      category: jobFull.category,
      closedAt: jobFull.closedAt,
      createdAt: jobFull.createdAt,
      departmentId: jobFull.departmentId,
      disqualifiedCandidatesCount: jobFull.disqualifiedCandidatesCount,
      description: jobFull.description,
      education: jobFull.education,
      employmentType: jobFull.employmentType,
      example: jobFull.example,
      experience: jobFull.experience,
      followed: jobFull.followed,
      guid: jobFull.guid,
      hiringManagerId: jobFull.hiringManagerId,
      hiredCandidatesCount: jobFull.hiredCandidatesCount,
      hiredCandidatesWithoutOpeningsCount: jobFull.hiredCandidatesWithoutOpeningsCount,
      hybrid: jobFull.hybrid,
      id: jobFull.id,
      issues: jobFull.issues,
      kind: jobFull.kind || OfferKind.Job,
      langCode: jobFull.langCode,
      locationIds: jobFull.locationIds,
      mailboxEmail: jobFull.mailboxEmail,
      numberOfOpenings: jobFull.numberOfOpenings,
      onSite: jobFull.onSite,
      pipelineTemplateId: jobFull.pipelineTemplateId,
      position: jobFull.position,
      publishedAt: jobFull.publishedAt,
      priority: jobFull.priority,
      qualifiedCandidatesCount: jobFull.qualifiedCandidatesCount,
      recruiterId: jobFull.recruiterId,
      remote: jobFull.remote,
      requirements: jobFull.requirements,
      sharedOpeningsCount: jobFull.sharedOpeningsCount,
      slug: jobFull.slug,
      status: jobFull.status,
      title: jobFull.title,
      updatedAt: jobFull.updatedAt,
      wysiwygEditor: jobFull.wysiwygEditor,
      jobPositionId: jobFull.jobPositionId,
    };
  }

  public static mapOfferItemToJobBasic(offerItem: OfferItem): JobBasic {
    return {
      candidatesCount: offerItem.candidatesCount,
      careersJobPageLayoutId: null,
      category: null,
      closedAt: offerItem.closedAt,
      createdAt: offerItem.createdAt,
      departmentId: offerItem.departmentId,
      description: offerItem.description,
      disqualifiedCandidatesCount: offerItem.disqualifiedCandidatesCount,
      education: null,
      employmentType: offerItem.employmentType,
      example: offerItem.example,
      experience: null,
      followed: offerItem.followed,
      guid: offerItem.guid,
      hiringManagerId: offerItem.hiringManagerId,
      hiredCandidatesCount: offerItem.hiredCandidatesCount,
      hiredCandidatesWithoutOpeningsCount: offerItem.hiredCandidatesWithoutOpeningsCount,
      hybrid: offerItem.hybrid,
      id: offerItem.id,
      issues: offerItem.issues,
      kind: offerItem.kind || OfferKind.Job,
      langCode: offerItem.langCode,
      locationIds: offerItem.locationIds,
      mailboxEmail: offerItem.mailboxEmail,
      numberOfOpenings: offerItem.numberOfOpenings,
      onSite: offerItem.onSite,
      pipelineTemplateId: offerItem.pipelineTemplateId!,
      position: offerItem.position,
      publishedAt: offerItem.publishedAt,
      priority: offerItem.priority,
      qualifiedCandidatesCount: offerItem.qualifiedCandidatesCount,
      recruiterId: offerItem.recruiterId,
      remote: offerItem.remote,
      requirements: offerItem.requirements,
      sharedOpeningsCount: offerItem.sharedOpeningsCount,
      slug: offerItem.slug,
      status: offerItem.status,
      title: offerItem.title,
      updatedAt: offerItem.updatedAt,
      wysiwygEditor: offerItem.wysiwygEditor,
      jobPositionId: offerItem.jobPositionId,
    };
  }

  public static mapJobBasicToJobItem(jobBasic: JobBasic, data: MapperAdditionalData): JobItem {
    return {
      candidatesCount: jobBasic.candidatesCount,
      createdAt: jobBasic.createdAt,
      departmentId: jobBasic.departmentId,
      department: data.department?.name || null,
      employmentType: jobBasic.employmentType,
      description: jobBasic.description,
      followed: jobBasic.followed,
      guid: jobBasic.guid,
      hiredCandidatesCount: jobBasic.hiredCandidatesCount,
      hiredCandidatesWithoutOpeningsCount: jobBasic.hiredCandidatesWithoutOpeningsCount,
      hiringManagerId: jobBasic.hiringManagerId,
      hiringManager: data.hiringManager,
      hybrid: jobBasic.hybrid,
      id: jobBasic.id,
      issues: jobBasic.issues,
      kind: jobBasic.kind,
      langCode: jobBasic.langCode,
      locationIds: jobBasic.locationIds,
      locations: data.locations,
      mailboxEmail: jobBasic.mailboxEmail,
      numberOfOpenings: jobBasic.numberOfOpenings,
      onSite: jobBasic.onSite,
      pipelineTemplateId: jobBasic.pipelineTemplateId,
      pipelineTemplate: data.pipelineTemplate,
      priority: jobBasic.priority,
      publishedAt: jobBasic.publishedAt,
      recruiterId: jobBasic.recruiterId,
      recruiter: data.recruiter,
      remote: jobBasic.remote,
      requirements: jobBasic.requirements,
      slug: jobBasic.slug,
      status: jobBasic.status,
      title: jobBasic.title,
      wysiwygEditor: jobBasic.wysiwygEditor,
      jobPositionId: jobBasic.jobPositionId,
    };
  }

  public static mapOfferItemToJobItem(offerItem: OfferItem, data: MapperAdditionalData): JobItem {
    return {
      candidatesCount: offerItem.candidatesCount,
      createdAt: offerItem.createdAt,
      departmentId: offerItem.departmentId,
      department: data.department?.name || null,
      description: offerItem.description,
      employmentType: offerItem.employmentType,
      followed: offerItem.followed,
      guid: offerItem.guid,
      hiredCandidatesCount: offerItem.hiredCandidatesCount,
      hiredCandidatesWithoutOpeningsCount: offerItem.hiredCandidatesWithoutOpeningsCount,
      hiringManagerId: offerItem.hiringManagerId,
      hiringManager: data.hiringManager,
      hybrid: offerItem.hybrid,
      id: offerItem.id,
      issues: offerItem.issues,
      kind: offerItem.kind,
      langCode: offerItem.langCode,
      locationIds: offerItem.locationIds,
      locations: data.locations,
      mailboxEmail: offerItem.mailboxEmail,
      numberOfOpenings: offerItem.numberOfOpenings,
      onSite: offerItem.onSite,
      pipelineTemplateId: offerItem.pipelineTemplateId,
      pipelineTemplate: data.pipelineTemplate,
      priority: offerItem.priority,
      publishedAt: offerItem.publishedAt,
      recruiterId: offerItem.recruiterId,
      recruiter: data.recruiter,
      remote: offerItem.remote,
      requirements: offerItem.requirements,
      slug: offerItem.slug,
      status: offerItem.status,
      title: offerItem.title,
      url: offerItem.url,
      wysiwygEditor: offerItem.wysiwygEditor,
      jobPositionId: offerItem.jobPositionId,
    };
  }

  public static mapOfferBriefToJobItem(
    offerBrief: OfferBrief,
    data: MapperAdditionalData,
  ): JobItem {
    return {
      candidatesCount: 0,
      createdAt: offerBrief.createdAt,
      departmentId: offerBrief.departmentId,
      department: data.department?.name || null,
      description: offerBrief.description,
      employmentType: null,
      followed: false,
      guid: offerBrief.guid,
      hiredCandidatesCount: 0,
      hiredCandidatesWithoutOpeningsCount: 0,
      hiringManagerId: offerBrief.hiringManagerId,
      hiringManager: data.hiringManager,
      hybrid: offerBrief.hybrid,
      id: offerBrief.id,
      kind: offerBrief.kind,
      langCode: offerBrief.langCode,
      locationIds: offerBrief.locationIds,
      locations: data.locations,
      mailboxEmail: offerBrief.mailboxEmail,
      numberOfOpenings: 0,
      onSite: offerBrief.onSite,
      pipelineTemplateId: offerBrief.pipelineTemplateId,
      pipelineTemplate: data.pipelineTemplate,
      priority: null,
      publishedAt: null,
      recruiterId: offerBrief.recruiterId,
      recruiter: data.recruiter,
      remote: offerBrief.remote,
      requirements: offerBrief.requirements,
      slug: offerBrief.slug,
      status: offerBrief.status,
      title: offerBrief.title,
      url: offerBrief.url,
      wysiwygEditor: 'prosemirror',
      jobPositionId: null,
    };
  }
}
